// @tennis-matches/services/winterdoppel/dateUtils

/** ISO-Kalenderwoche */
export function isoWeekNumber(dateInput) {
  const d =
    dateInput instanceof Date ? new Date(dateInput) : new Date(dateInput);
  if (Number.isNaN(d.getTime())) return null;
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
}

/** ISO-Wochentag 1=Mo … 7=So */
export function isoWeekday(dateInput) {
  const d =
    dateInput instanceof Date ? new Date(dateInput) : new Date(dateInput);
  if (Number.isNaN(d.getTime())) return null;
  const js = d.getDay();
  return js === 0 ? 7 : js;
}

export function toIsoDate(dateInput) {
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function parseIsoDate(iso) {
  const s = String(iso || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const d = new Date(`${s}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Alle Termine im Saisonfenster, deren ISO-Wochentag in weekdays liegt.
 * @param {{ seasonStart: string, seasonEnd: string, weekdays: number[] }} opts
 * @returns {string[]} ISO dates
 */
export function generateSessionDates({ seasonStart, seasonEnd, weekdays }) {
  const start = parseIsoDate(seasonStart);
  const end = parseIsoDate(seasonEnd);
  const days = new Set(
    (Array.isArray(weekdays) ? weekdays : [])
      .map((n) => Number(n))
      .filter((n) => n >= 1 && n <= 7),
  );
  if (!start || !end || days.size === 0) return [];
  const out = [];
  const cur = new Date(start);
  while (cur <= end) {
    if (days.has(isoWeekday(cur))) out.push(toIsoDate(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

export function playerAvailableOnDate(player, blocks, isoDate) {
  if (!player || player.interest_status === "no") return false;
  const list = Array.isArray(blocks) ? blocks : [];
  for (const b of list) {
    if (String(b?.reason || "") === "week_rule_override") continue;
    const s = String(b.start_date || b.startDate || "");
    const e = String(b.end_date || b.endDate || s);
    if (s && e && isoDate >= s && isoDate <= e) return false;
  }
  if (isSessionDateExcludedByWeekRule(player.week_rule, isoDate)) {
    return list.some((b) => {
      if (String(b?.reason || "") !== "week_rule_override") return false;
      const s = String(b.start_date || b.startDate || "");
      const e = String(b.end_date || b.endDate || s);
      return Boolean(s && e && isoDate >= s && isoDate <= e);
    });
  }
  return true;
}

/** true = Termin fällt durch gerade/ungerade-KW-Regel aus. */
export function isSessionDateExcludedByWeekRule(weekRule, isoDate) {
  const d = parseIsoDate(isoDate);
  if (!d) return false;
  const kw = isoWeekNumber(d);
  if (kw == null) return false;
  const rule = String(weekRule || "all");
  if (rule === "even_kw") return kw % 2 !== 0;
  if (rule === "odd_kw") return kw % 2 === 0;
  return false;
}

/** Termin liegt vor dem Stichtag (exklusiv: am Spieltag selbst noch „aktuell“). */
export function isWinterDoppelSessionElapsed(sessionDate, asOfDate) {
  const d = String(sessionDate || "").slice(0, 10);
  const asOf = String(asOfDate || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d) || !/^\d{4}-\d{2}-\d{2}$/.test(asOf)) {
    return false;
  }
  return d < asOf;
}

/**
 * Termine für die Planungs-Kartenliste (ohne bereits vergangene Tage).
 * @param {Array<{ session_date?: string, status?: string }>} sessions
 * @param {string} [asOfDate] YYYY-MM-DD
 */
export function listWinterDoppelOpenPlanningSessions(sessions, asOfDate) {
  const asOf = String(asOfDate || "").slice(0, 10);
  return (Array.isArray(sessions) ? sessions : []).filter((session) => {
    if (String(session?.status || "") === "cancelled") return false;
    return !isWinterDoppelSessionElapsed(session?.session_date, asOf);
  });
}
